import { ConditionalDisplay, type PassCondition } from '@rsos/base-components';
import { useFlags } from '@rsos/flags';
import { useActiveCapabilities } from '@rsos/reduxDataStore/src/features/capstone/psapInfo';
import { useUserPermissions } from '@rsos/sinatra/src/selectors';

interface ConditionalDisplayProps
  extends Omit<React.ComponentProps<typeof ConditionalDisplay>, 'conditions'> {
  conditions: Condition[];
}

/**
 * A wrapper around @see ConditionalDisplay that supports access-controlled
 * display based on feature flags, capabilities, and permissions, in addition to
 * static conditions.
 */
export default function ConditionalFeatureDisplay({
  dataName,
  conditions,
  children,
}: ConditionalDisplayProps) {
  const resolvedConditions = useResolvedConditions(conditions);

  return (
    <ConditionalDisplay
      dataName={dataName}
      conditions={resolvedConditions.map(({ showIf, reason }) => ({
        showIf,
        reason,
      }))}
    >
      {children}
    </ConditionalDisplay>
  );
}

interface FeatureFlagEnabledCondition {
  featureFlagEnabled: string;
}

interface FeatureFlagDisabledCondition {
  featureFlagDisabled: string;
}

interface CapabilityEnabledCondition {
  capabilityEnabled: string;
}

interface CapabilityDisabledCondition {
  capabilityDisabled: string;
}

interface PermissionEnabledCondition {
  permissionEnabled: string;
}

interface PermissionDisabledCondition {
  permissionDisabled: string;
}

type Condition =
  | PassCondition
  | FeatureFlagEnabledCondition
  | FeatureFlagDisabledCondition
  | CapabilityEnabledCondition
  | CapabilityDisabledCondition
  | PermissionEnabledCondition
  | PermissionDisabledCondition;

/**
 * Resolves the various condition types (feature flags, capabilities, permissions) into a uniform @see PassCondition format.
 */
function useResolvedConditions(conditions: Condition[]): PassCondition[] {
  const flags = useFlags();
  const activeCapabilities = useActiveCapabilities();
  const activePermissions = useUserPermissions();

  const resolvedConditions = conditions.map(condition => {
    if ('showIf' in condition) {
      return { ...condition };
    } else if ('featureFlagEnabled' in condition) {
      const flagName = condition.featureFlagEnabled.toLowerCase();
      const isEnabled = Boolean(flags?.[flagName]);
      return {
        showIf: isEnabled,
        reason: `feature-flag-disabled:${flagName}`,
      };
    } else if ('featureFlagDisabled' in condition) {
      const flagName = condition.featureFlagDisabled.toLowerCase();
      const isEnabled = Boolean(flags?.[flagName]);
      return {
        showIf: !isEnabled,
        reason: `feature-flag-enabled:${flagName}`,
      };
    } else if ('capabilityEnabled' in condition) {
      const capabilityName = condition.capabilityEnabled;
      const isEnabled = Boolean(activeCapabilities?.[capabilityName]);
      return {
        showIf: isEnabled,
        reason: `capability-disabled:${capabilityName}`,
      };
    } else if ('capabilityDisabled' in condition) {
      const capabilityName = condition.capabilityDisabled;
      const isEnabled = Boolean(activeCapabilities?.[capabilityName]);
      return {
        showIf: !isEnabled,
        reason: `capability-enabled:${capabilityName}`,
      };
    } else if ('permissionEnabled' in condition) {
      const permissionName = condition.permissionEnabled;
      const isEnabled = activePermissions.includes(permissionName);
      return {
        showIf: isEnabled,
        reason: `permission-denied:${permissionName}`,
      };
    } else if ('permissionDisabled' in condition) {
      const permissionName = condition.permissionDisabled;
      const isEnabled = activePermissions.includes(permissionName);
      return {
        showIf: !isEnabled,
        reason: `permission-granted:${permissionName}`,
      };
    } else {
      // Using `never` here to enforce that all condition types are handled above at compile time.
      const _exhaustiveCheck: never = condition;
      // eslint-disable-next-line no-console -- Unhandled condition type; log for Sentry visibility (although it should be impossible as long as we typecheck the code before deploy, but...).
      console.error(
        'ConditionalFeatureDisplay: Unhandled condition type',
        _exhaustiveCheck,
      );
      return {
        showIf: false,
        reason: 'unknown-condition',
      };
    }
  });

  return resolvedConditions;
}
